import { showToast } from '@/utils/notification';
import { del, get, post, put } from '@/utils/request';
import { defineStore } from 'pinia';

export const useGroupsStore = defineStore('groups', {
  state: () => ({
    groups: [] as any,
    groupsWithMetric: [] as any,
    error: null as any | null,
    page_number: 1,
    page_size: 10,
    totalPageFromSearch: 0,
    selectedCreators: [] as any[],
    activeTab: 'Instagram',
    cache: {} as Record<string, { data: any; timestamp: number | null }>,
    cacheConfig: {
      duration: 5 * 60 * 1000, // 5 minutes - adjust as needed
    },
    isRefreshing: false,
    groupCreatorsCallCount: 0,
  }),
  actions: {
    cleanEmptyFields(obj: any) {
      const result = { ...obj };
      Object.keys(result).forEach(key => {
        if (
          result[key] === '' ||
          result[key] === null ||
          result[key] === undefined ||
          Number.isNaN(result[key]) ||
          (Array.isArray(result[key]) && result[key].length === 0) ||
          (typeof result[key] === 'object' && result[key] !== null && Object.keys(result[key]).length === 0) ||
          ((key === 'engagement_rate_from' || key === 'engagement_rate_to') && result[key] === 0)
        ) {
          delete result[key];
        }
      });
      return result;
    },

    async doGetGroups(forceRefresh = false) {
      const cacheKey = 'groups';

      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCacheValid(cacheKey)) {
        this.groups = this.cache[cacheKey].data;
        return this.groups;
      }

      // Fetch fresh data
      this.error = null;

      try {
        const freshGroups = await get('/merchant/influencer_groups');
        this.groups = freshGroups;

        // Cache the result
        this.cache[cacheKey] = {
          data: freshGroups,
          timestamp: Date.now(),
        };

        return this.groups;
      } catch (e) {
        this.error = e;

        // Optionally return cached data on error if available
        if (this.cache[cacheKey]?.data) {
          this.groups = this.cache[cacheKey].data;
          return this.groups;
        }

        throw e; // Re-throw if no cached data available
      }
    },

    async doGetGroupsWithMetric(forceRefresh = false) {
      const cacheKey = 'groupsWithMetric';

      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCacheValid(cacheKey)) {
        this.groupsWithMetric = this.cache[cacheKey].data;
        return this.groupsWithMetric;
      }

      // Fetch fresh data
      this.error = null;

      try {
        const response = await get('/influencer_groups?include_metrics=true');
        const results = response.results;

        this.groupsWithMetric = results;

        // Cache the result
        this.cache[cacheKey] = {
          data: results,
          timestamp: Date.now(),
        };

        return this.groupsWithMetric;
      } catch (e) {
        this.error = e;

        // Optionally return cached data on error if available
        if (this.cache[cacheKey]?.data) {
          this.groupsWithMetric = this.cache[cacheKey].data;
          return this.groupsWithMetric;
        }

        throw e; // Re-throw if no cached data available
      }
    },

    async doGetGroupDetails(id: string) {
      try {
        const response = await get(`/influencer_groups/${id}`);

        return response;
      } catch (e) {
        this.error = e;

        throw e;
      }
    },

    async doAddCreatorsToGroup(groupId: string, influencerIds: string[]) {
      this.error = null;
      let data = {};
      try {
        data = await post(`/influencer_groups/${groupId}/influencers`, {
          influencer_ids: influencerIds,
        });
      } catch (e) {
        this.error = e;
        const error = e as any;

        if (error.message) {
          return showToast({
            type: 'error',
            message: error.message,
          });
        }
        return showToast({
          type: 'error',
          message: 'Something went wrong. Please try again later.',
        });
      }
      return data;
    },

    async doGetGroup(form: { influencer_group_id: string; page_number: number; page_size: number }) {
      this.error = null;
      let data = {};
      try {
        const params = new URLSearchParams('');
        params.append('page_number', form.page_number.toString());
        params.append('page_size', form.page_size.toString());

        data = await get(`/merchant/influencer_groups/${form.influencer_group_id}?${params.toString()}`);
      } catch (e) {
        this.error = e;
      }
      return data;
    },

    async doGetGroupCreators(group_id: string) {
      this.error = null;

      // Increment call count and set isRefreshing if this is the second or subsequent call
      this.groupCreatorsCallCount++;
      if (this.groupCreatorsCallCount > 1) {
        this.isRefreshing = true;
      }

      let data = [];
      try {
        const res = await get(
          `/influencer_groups/${group_id}/influencers?page_number=${this.page_number}&page_size=${this.page_size}&channel=${this.activeTab.toUpperCase()}`,
        );
        this.totalPageFromSearch = res.total_pages;
        data = res.results;
      } catch (e) {
        this.error = e;
      } finally {
        // Always set isRefreshing to false after the call is done
        this.isRefreshing = false;
      }

      return data;
    },

    async doCreateGroup(form: any) {
      this.error = null;
      let data = {};
      try {
        data = await post('/merchant/influencer_groups/', form);
        this.groups?.push(data);
      } catch (e) {
        this.error = e;
        const error = e as any;

        if (error.message) {
          return showToast({
            type: 'error',
            message: error.message,
          });
        }
        return showToast({
          type: 'error',
          message: 'Something went wrong. Please try again later.',
        });
      }
      return data;
    },

    async doUpdateGroup(groupId: any, form: any) {
      this.error = null;
      let data = {};

      try {
        const cleanedForm = this.cleanEmptyFields(form);

        data = await put(`/merchant/influencer_groups/${groupId}`, cleanedForm);
      } catch (e) {
        this.error = e;
        const error = e as any;

        if (error.message) {
          return showToast({
            type: 'error',
            message: error.message,
          });
        }
        return showToast({
          type: 'error',
          message: 'Something went wrong. Please try again later.',
        });
      }
      return data;
    },

    async doRegenerateCode(form: any) {
      this.error = null;
      let data = {};
      try {
        data = await put(`/merchant/influencer_groups/${form.id}/replace_code/`, null);
      } catch (e) {
        this.error = e;
      }
      return data;
    },

    async doDeleteInfluencerFromGroup(form: any) {
      this.error = null;
      try {
        await del(`/merchant/influencer_groups/${form.id}/influencers/${form.influencer_id}`);
      } catch (e) {
        console.error(e);
      }
    },

    async doDeleteCreatorsFromGroup(groupId: any, creatorIds: any[]) {
      this.error = null;
      try {
        await del(`/influencer_groups/${groupId}/influencers`, {
          influencer_ids: creatorIds,
        });
      } catch (e) {
        console.error(e);
      }
    },

    async doDeleteGroup(id: string) {
      this.error = null;
      try {
        await del(`/merchant/influencer_groups/${id}`);
        await this.doGetGroups(true); // Refresh groups after deletion
        // this.groups = this.groups?.filter((e: any) => e.id != id) ?? this.groups;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },
  },
  getters: {
    isCacheValid: state => (cacheKey: any) => {
      const entry = state.cache[cacheKey];
      return entry?.data && entry?.timestamp && Date.now() - entry.timestamp < state.cacheConfig.duration;
    },
  },
});
